/**
 * Light/dark theming.
 *
 * The prototype flipped a single class on the shell div (`bp` / `bp dark`) and let
 * CSS custom properties do the rest — production keeps exactly that mechanism, so a
 * theme change can never require a component to know which theme is active.
 *
 * Light is the default, including on first visit for a user whose OS prefers dark:
 * the product is a light-first tool and the toggle is one click away.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "hrms-theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    // Private-mode or blocked storage — the session simply starts light.
    return "light";
  }
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Extra classes for the themed root — the shell adds its own layout class here. */
  className?: string;
  /** Test seam: forces a theme and skips storage. */
  initialTheme?: Theme;
}

export function ThemeProvider({ children, className, initialTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => initialTheme ?? readStoredTheme());

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Persistence is a nicety; the flip itself must always work.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Native widgets (scrollbars, date pickers, form controls) read color-scheme from
  // the document, not from our wrapper — so it is set in both places.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.colorScheme = theme;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  const rootClass = ["bp", theme === "dark" ? "dark" : null, className].filter(Boolean).join(" ");

  return (
    <ThemeContext.Provider value={value}>
      <div className={rootClass} style={{ colorScheme: theme }} data-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside <ThemeProvider>.");
  return context;
}
